import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass";

const COUNT = 12000;

export default function MorphingShape() {
  const containerRef = useRef(null);
  const morphData = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    // Remove any existing canvas first
    const oldCanvas = containerRef.current.querySelector("canvas");
    if (oldCanvas) {
      containerRef.current.removeChild(oldCanvas);
    }


    /* ---------------- SCENE ---------------- */
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x04091E); // dark theme background
    const camera = new THREE.PerspectiveCamera(
      60,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.z = 80;

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true
    });
    renderer.setSize(
      containerRef.current.clientWidth,
      containerRef.current.clientHeight
    );
    containerRef.current.appendChild(renderer.domElement);

    /* ---------------- CONTROLS ---------------- */
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.enableZoom = false;

    /* ---------------- PARTICLES ---------------- */
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(COUNT * 3);
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      size: 0.6,
      color: "#ffffff", // bright white for particles
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending, // gives glowing effect
      depthWrite: false
    });
    const light = new THREE.PointLight(0xffffff, 0.3);
    light.position.set(50, 250, 50);
    scene.add(light);
    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    /* ---------------- SHAPES ---------------- */
    const sphere = () => {
      const pts = [];
      for (let i = 0; i < COUNT; i++) {
        const r = 35;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        pts.push(
          new THREE.Vector3(
            r * Math.sin(phi) * Math.cos(theta),
            r * Math.sin(phi) * Math.sin(theta),
            r * Math.cos(phi)
          )
        );
      }
      return pts;
    };

    const torus = () => {
      const geo = new THREE.TorusKnotGeometry(25, 8, 200, 16);
      const pts = [];
      const attr = geo.attributes.position;
      for (let i = 0; i < COUNT; i++) {
        const id = i % attr.count;
        pts.push(
          new THREE.Vector3().fromBufferAttribute(attr, id)
        );
      }
      return pts;
    };

    const heart = () => {
      const pts = [];
      const scale = 2; // size of heart
      for (let i = 0; i < COUNT; i++) {
        const t = Math.random() * Math.PI * 2;
        const s = Math.random();
        const x = scale * 16 * Math.pow(Math.sin(t), 3);
        const y = scale * (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
        const z = (Math.random() - 0.5) * 10; // depth
        pts.push(new THREE.Vector3(x * s, y * s, z));
      }
      return pts;
    };

    const star = () => {
      const pts = [];
      const scale = 35;
      for (let i = 0; i < COUNT; i++) {
        const angle = Math.random() * Math.PI * 2;
        const radius = Math.random() * scale;
        const spikes = 5;
        const r = radius * (Math.sin(spikes * angle) > 0 ? 1 : 0.5);
        const x = r * Math.cos(angle);
        const y = r * Math.sin(angle);
        const z = (Math.random() - 0.5) * 5;
        pts.push(new THREE.Vector3(x, y, z));
      }
      return pts;
    };

    const laptop = () => {
      const pts = [];
      const w = 50, h = 30, d = 3;

      for (let i = 0; i < COUNT; i++) {
        const isScreen = Math.random() > 0.3;

        if (isScreen) {
          const x = (Math.random() - 0.5) * w;
          const y = (Math.random() - 0.5) * h;
          const z = d;
          pts.push(new THREE.Vector3(x, y, z));
        } else {
          const x = (Math.random() - 0.5) * (w + 10);
          const y = -h / 2 - 3;
          const z = (Math.random() - 0.5) * 10;
          pts.push(new THREE.Vector3(x, y, z));
        }
      }
      return pts;
    };


    const megaphone = () => {
      const pts = [];

      for (let i = 0; i < COUNT; i++) {
        const t = Math.random();
        const angle = Math.random() * Math.PI * 2;

        const radius = 5 + t * 20;
        const x = radius * Math.cos(angle);
        const y = radius * Math.sin(angle);
        const z = t * 40 - 20;

        pts.push(new THREE.Vector3(x, y, z));
      }
      return pts;
    };


   



    const SHAPES = [
      sphere, heart,
      laptop,
      megaphone,
      torus,
      star
    ];

    let current = 0;

    const applyShape = pts => {
      pts.forEach((p, i) => {
        positions[i * 3] = p.x;
        positions[i * 3 + 1] = p.y;
        positions[i * 3 + 2] = p.z;
      });
      geometry.attributes.position.needsUpdate = true;
    };

    // Create initial "from" points (all at center)
    const initialPts = [];
    for (let i = 0; i < COUNT; i++) {
      initialPts.push(new THREE.Vector3(0, 0, 0));
    }

    // Get the target shape points (first shape)
    const targetPts = SHAPES[current]();

    // Apply initial positions (at center)
    initialPts.forEach((p, i) => {
      positions[i * 3] = p.x;
      positions[i * 3 + 1] = p.y;
      positions[i * 3 + 2] = p.z;
    });
    geometry.attributes.position.needsUpdate = true;

    // Set up morphing from center to first shape
    let morphing = true;
    let progress = 0;
    particles.userData = {
      from: initialPts.flatMap(p => [p.x, p.y, p.z]),
      to: targetPts.flatMap(p => [p.x, p.y, p.z]),
      next: current
    };




    morphData.current = () => {
      if (morphing) return;

      morphing = true;
      progress = 0;

      const next = (current + 1) % SHAPES.length;
      const from = positions.slice();
      const toPts = SHAPES[next]();
      const to = new Float32Array(COUNT * 3);

      toPts.forEach((p, i) => {
        to[i * 3] = p.x;
        to[i * 3 + 1] = p.y;
        to[i * 3 + 2] = p.z;
      });

      particles.userData = { from, to, next };
    };

    /* ---------------- POST ---------------- */
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    composer.addPass(
      new UnrealBloomPass(
        new THREE.Vector2(containerRef.current.clientWidth, containerRef.current.clientHeight),
        0.1, // intensity, increase for more glow
        0.1,
        10
      )
    );

    /* ---------------- LOOP ---------------- */
    const animate = () => {
      requestAnimationFrame(animate);

      if (morphing) {
        progress += 0.025;
        const e = Math.min(progress, 1);
        const { from, to, next } = particles.userData;

        for (let i = 0; i < positions.length; i++) {
          positions[i] = from[i] + (to[i] - from[i]) * e;
        }
        geometry.attributes.position.needsUpdate = true;

        if (e === 1) {
          current = next;
          morphing = false;
        }
      }

      controls.update();
      composer.render();
    };

    animate();
    const interval = setInterval(() => {
      if (morphData.current) {
        morphData.current();
      }
    }, 3000);

    /* ---------------- RESIZE ---------------- */
    const resize = () => {
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      composer.setSize(w, h);
    };
    window.addEventListener("resize", resize);

    /* ---------------- CLEANUP ---------------- */
    // SAVE CANVAS REFERENCE
    const canvas = renderer.domElement;

    /* ---------------- CLEANUP ---------------- */
    return () => {
      clearInterval(interval);

      window.removeEventListener("resize", resize);

      controls.dispose();
      geometry.dispose();
      material.dispose();
      renderer.dispose();

      if (containerRef.current && canvas.parentNode === containerRef.current) {
        containerRef.current.removeChild(canvas);
      }
    };


  }, []);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

    </div>
  );
}
